import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { Button } from '../components/ui/button';
import { 
  HeartPulse, 
  Users, 
  Truck, 
  MapPin,
  TrendingUp,
  ArrowRight
} from 'lucide-react';

const mainStats = [
  { value: 150000, icon: HeartPulse, label: 'Screenings Completed', suffix: '+' },
  { value: 500, icon: Users, label: 'Corporate Clients', suffix: '+' },
  { value: 45, icon: Truck, label: 'Mobile Units', suffix: '' },
  { value: 120, icon: MapPin, label: 'Cities Covered', suffix: '+' },
];

const impactStats = [
  { value: 12500, label: 'Early Detections', suffix: '+' },
  { value: 98, label: 'Satisfaction Rate', suffix: '%' },
  { value: 30, label: 'Cost Reduction', suffix: '%' },
];

const outcomes = [
  { label: 'Cardiovascular Issues', value: 8500, percentage: 85 },
  { label: 'Pre-Diabetic Conditions', value: 6200, percentage: 72 },
  { label: 'Cancer Indicators', value: 2100, percentage: 94 },
  { label: 'Mental Health Referrals', value: 4800, percentage: 68 },
];

export const Statistics = () => {
  return (
    <div className="bg-zinc-950 min-h-screen pt-20">
      {/* Hero */}
      <section className="py-32 relative overflow-hidden" data-testid="stats-hero">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/5 rounded-full blur-[200px]" />
        
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="overline mb-6 block">Our Impact</span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8 font-['Outfit'] tracking-tighter">
              Numbers that<br />
              <span className="gradient-text">tell our story</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl mx-auto">
              Every statistic represents lives improved through preventative care.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Stats */}
      <section className="py-24 border-t border-white/5" data-testid="main-stats-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mainStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-surface rounded-2xl p-8 text-center"
                data-testid={`main-stat-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                  <stat.icon className="w-7 h-7 text-emerald-500" />
                </div>
                <p className="stat-display text-white mb-2">
                  <CountUp end={stat.value} duration={2.5} enableScrollSpy scrollSpyOnce separator="," />
                  <span className="text-emerald-500">{stat.suffix}</span>
                </p>
                <p className="text-zinc-500 text-sm uppercase tracking-wide">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Banner */}
      <section className="py-24 bg-zinc-900/50" data-testid="impact-banner">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {impactStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`impact-stat-${index}`}
              >
                <p className="text-6xl md:text-7xl lg:text-8xl font-bold font-['JetBrains_Mono'] text-white">
                  <CountUp end={stat.value} duration={2} enableScrollSpy scrollSpyOnce separator="," />
                  <span className="text-emerald-500">{stat.suffix}</span>
                </p>
                <p className="text-zinc-500 mt-4 text-sm uppercase tracking-wide">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-32" data-testid="outcomes-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="overline mb-4 block">Health Outcomes</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tighter">
              Early detection saves lives
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            {outcomes.map((outcome, index) => (
              <motion.div
                key={outcome.label}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-surface rounded-2xl p-8"
                data-testid={`outcome-${index}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-white font-medium font-['Outfit']">{outcome.label}</h3>
                  <span className="text-2xl font-bold text-emerald-500 font-['JetBrains_Mono']">
                    <CountUp end={outcome.value} duration={2} enableScrollSpy scrollSpyOnce separator="," />
                  </span>
                </div>
                <div className="w-full bg-zinc-800 rounded-full h-2 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${outcome.percentage}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.3 }}
                    className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                  />
                </div>
                <p className="text-xs text-zinc-600 mt-2">{outcome.percentage}% intervention rate</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI */}
      <section className="py-32 border-t border-white/5" data-testid="roi-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="overline mb-4 block">ROI</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 font-['Outfit'] tracking-tighter">
                Proven return on investment
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Organizations that partner with us see measurable improvements 
                in employee health and significant cost savings.
              </p>
              
              <div className="space-y-4">
                {[
                  { label: 'Healthcare cost reduction', value: '30%' },
                  { label: 'Decrease in sick days', value: '25%' },
                  { label: 'Productivity improvement', value: '18%' },
                  { label: 'Retention increase', value: '15%' },
                ].map((item, index) => (
                  <div key={item.label} className="flex items-center justify-between bg-zinc-900/50 border border-white/5 rounded-xl p-4" data-testid={`roi-stat-${index}`}>
                    <span className="text-zinc-400">{item.label}</span>
                    <span className="text-xl font-bold text-emerald-500 font-['JetBrains_Mono']">{item.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-emerald-500/10 to-transparent rounded-3xl p-10 border border-emerald-500/20"
            >
              <TrendingUp className="w-14 h-14 text-emerald-500 mb-6" />
              <h3 className="text-4xl font-bold text-white mb-4 font-['JetBrains_Mono']">$4.2M</h3>
              <p className="text-zinc-400 mb-6">
                Average annual healthcare savings for companies with 1,000+ employees.
              </p>
              <p className="text-xs text-zinc-600">Based on 2024 client data</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 relative overflow-hidden" data-testid="stats-cta">
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/5 to-transparent" />
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Outfit'] tracking-tighter">
              Be part of our story
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Join hundreds of organizations benefiting from preventative health programs.
            </p>
            <Link to="/request-screening" data-testid="stats-cta-btn">
              <Button className="rounded-full px-8 py-6 text-base font-medium bg-emerald-500 hover:bg-emerald-400 text-black transition-all duration-200 glow-emerald">
                Start Your Program
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
